import type {
  VoucherEligibilityItem,
  VoucherGetByCodeResponse,
  VoucherGetEligibilitiesItem,
} from '@diamond/shared/types';

type VoucherDisplayItem =
  | VoucherEligibilityItem
  | VoucherGetByCodeResponse
  | VoucherGetEligibilitiesItem;

type VoucherTrackingType =
  | 'discount_nominal'
  | 'discount_percentage'
  | 'free_goods';

const REWARD_TYPE_TO_TRACKING_TYPE: Record<string, VoucherTrackingType> = {
  DISCOUNT_PERCENT: 'discount_percentage',
  DISCOUNT_AMOUNT: 'discount_nominal',
  FREE_GOODS: 'free_goods',
};

function isRewardTypeDiscount(rewardType?: string): boolean {
  return rewardType === 'DISCOUNT_PERCENT' || rewardType === 'DISCOUNT_AMOUNT';
}

/**
 * Extract fixed voucher tracking params from a voucher object.
 * Reusable across all voucher-related analytics events.
 *
 * Rules per voucher type:
 * - DISCOUNT_AMOUNT: percentage = null, discount = nominal value
 * - DISCOUNT_PERCENT: percentage = filled, discount = calculated
 * - FREE_GOODS: percentage = null, discount = null
 */
export const getVoucherTrackingParams = (voucher: VoucherDisplayItem) => {
  const rewardType = 'reward' in voucher ? voucher.reward?.type : undefined;
  const discountPercent =
    'reward' in voucher ? voucher.reward?.discount_percent : undefined;
  const discountAmount =
    'reward' in voucher ? voucher.reward?.discount_amount : undefined;

  const trackingType = rewardType
    ? REWARD_TYPE_TO_TRACKING_TYPE[rewardType] ?? '-'
    : '-';

  const isFreeGoods = trackingType === 'free_goods';

  // Nominal monetary benefit of the voucher. Mirrors `discount`: free-goods
  // vouchers have no nominal value, so it stays null.
  const nominalValue = isFreeGoods ? null : discountAmount ?? null;

  return {
    voucher_id: voucher.code,
    voucher_name: voucher.title,
    voucher_type: trackingType,
    currency: 'IDR',
    value: nominalValue,
    discount: nominalValue,
    discount_percentage:
      isFreeGoods || !isRewardTypeDiscount(rewardType)
        ? null
        : discountPercent ?? null,
  };
};
